import { useMemo } from 'react';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  hideNewTaskRow?: boolean;
};

export const useTasksTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  hideNewTaskRow,
}: Options) =>
  useMemo(() => {
    const customTaskOrder = Object.values(customOrder ?? {}).flat();
    const taskTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
      return acc;
    }, {});

    // Add New Task row
    const allTreeItems = { ...taskTreeItems };
    const children = Object.keys(taskTreeItems);

    if (!hideNewTaskRow) {
      const newTaskItemId = 'new-task';
      children.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: 'New Task',
        type: TreeItemTypes.NewTask,
      };
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children,
      },
      ...allTreeItems,
    };
  }, [customOrder, sortingMode, tasks, today, weekStartsOn, hideNewTaskRow]);
